import React from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../../../../components/ui/dialog";
import { Button } from "../../../../../components/ui/button";
import { DocumentEdit } from "tekkr-common/dist/model/attachments";
import { AlertTriangleIcon, CheckIcon, XIcon } from "lucide-react";
import { InlineDiffView } from "./inline-diff-view";
import { cn } from "../../../../../lib/utils";
import Spinner from "../../../../../components/ui/spinner";

export type EditState = "pending" | "accepted" | "rejected";

type ReviewMode = {
    mode: "review";
    editStates: Record<string, EditState>;
    onEditStateChange: (id: string, state: EditState) => void;
    onApply: () => void;
    isApplying: boolean;
    applyError: string | null;
};

type AppliedMode = {
    mode: "applied";
    appliedEditIds: string[];
};

export type DialogMode = ReviewMode | AppliedMode;

interface DocumentUpdateReviewDialogProps {
    open: boolean;
    onClose: () => void;
    proposal: {
        proposalId: string;
        documentName: string;
        edits: DocumentEdit[];
        userChangesDetected: boolean;
        changeSummary: string;
    };
    dialogMode: DialogMode;
}

export function DocumentUpdateReviewDialog({ open, onClose, proposal, dialogMode }: DocumentUpdateReviewDialogProps) {
    const { edits } = proposal;
    const isReview = dialogMode.mode === "review";

    const acceptedCount = isReview ? Object.values(dialogMode.editStates).filter((s) => s === "accepted").length : 0;

    const handleAcceptAll = () => {
        if (!isReview) return;
        edits.forEach((edit) => {
            dialogMode.onEditStateChange(edit.id, "accepted");
        });
    };

    const handleRejectAll = () => {
        if (!isReview) return;
        edits.forEach((edit) => {
            dialogMode.onEditStateChange(edit.id, "rejected");
        });
    };

    const dialogTitle = isReview
        ? `Review Updates: ${proposal.documentName}`
        : `Applied Changes: ${proposal.documentName}`;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 p-0">
                <DialogHeader className="space-y-3 border-b px-6 py-4">
                    <div>
                        <DialogTitle className="text-lg">{dialogTitle}</DialogTitle>
                        <DialogDescription className="mt-1 line-clamp-2 text-sm">
                            {proposal.changeSummary}
                        </DialogDescription>
                    </div>

                    {isReview && proposal.userChangesDetected && (
                        <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-sm text-amber-700 dark:text-amber-400">
                            <AlertTriangleIcon className="h-4 w-4 shrink-0" />
                            <span>User edits detected. Review changes carefully.</span>
                        </div>
                    )}

                    {isReview && (
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={handleAcceptAll}>
                                Accept All
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleRejectAll}>
                                Reject All
                            </Button>
                        </div>
                    )}

                    {!isReview && (
                        <div className="text-sm text-muted-foreground">
                            {dialogMode.appliedEditIds.length} of {edits.length} changes were applied
                        </div>
                    )}
                </DialogHeader>

                <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
                    {edits.map((edit) => (
                        <EditCard
                            key={edit.id}
                            edit={edit}
                            mode={dialogMode.mode}
                            state={isReview ? dialogMode.editStates[edit.id] : undefined}
                            onStateChange={isReview ? (s) => dialogMode.onEditStateChange(edit.id, s) : undefined}
                            isApplied={!isReview ? dialogMode.appliedEditIds.includes(edit.id) : undefined}
                        />
                    ))}

                    {edits.length === 0 && (
                        <div className="py-8 text-center text-sm text-muted-foreground">No changes found.</div>
                    )}
                </div>

                <DialogFooter className="flex-col items-stretch gap-2 border-t px-6 py-4">
                    {isReview ? (
                        <>
                            {dialogMode.applyError && (
                                <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
                                    <AlertTriangleIcon className="h-4 w-4 shrink-0" />
                                    <span>{dialogMode.applyError}</span>
                                </div>
                            )}
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={onClose} disabled={dialogMode.isApplying}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={dialogMode.onApply}
                                    disabled={acceptedCount === 0 || dialogMode.isApplying}
                                >
                                    {dialogMode.isApplying ? (
                                        <>
                                            <Spinner className="mr-2 h-4 w-4" />
                                            Applying...
                                        </>
                                    ) : acceptedCount === 0 ? (
                                        "Apply Changes"
                                    ) : (
                                        `Apply ${acceptedCount} ${acceptedCount === 1 ? "Change" : "Changes"}`
                                    )}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button variant="outline" onClick={onClose}>
                            Close
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

interface EditCardProps {
    edit: DocumentEdit;
    mode: "review" | "applied";
    state?: EditState;
    onStateChange?: (state: EditState) => void;
    isApplied?: boolean;
}

function EditCard({ edit, mode, state, onStateChange, isApplied }: EditCardProps) {
    const isReview = mode === "review";

    const cardClass = cn(
        "rounded-lg border p-3 transition-all",
        isReview && state === "pending" && "border-border",
        isReview && state === "accepted" && "border-confirmation bg-confirmation/5",
        isReview && state === "rejected" && "border-destructive/50 bg-destructive/5 opacity-60",
        !isReview && isApplied && "border-confirmation bg-confirmation/5",
        !isReview && !isApplied && "border-border/50 opacity-50"
    );

    const reasonClass = cn(
        "text-sm font-medium leading-tight",
        isReview && state === "rejected" && "line-through opacity-70",
        !isReview && !isApplied && "line-through opacity-70"
    );

    return (
        <div className={cardClass}>
            <div className="mb-2 flex items-start justify-between gap-3">
                <span className={reasonClass}>{edit.reason}</span>
                {isReview ? (
                    <ReviewActions state={state!} onStateChange={onStateChange!} />
                ) : (
                    <AppliedBadge isApplied={isApplied!} />
                )}
            </div>
            <InlineDiffView oldText={edit.searchText} newText={edit.replaceText} />
        </div>
    );
}

interface ReviewActionsProps {
    state: EditState;
    onStateChange: (state: EditState) => void;
}

function ReviewActions({ state, onStateChange }: ReviewActionsProps) {
    if (state === "pending") {
        return (
            <div className="flex shrink-0 gap-1">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStateChange("accepted")}
                    className="h-7 gap-1 px-2 text-confirmation hover:bg-confirmation/10 hover:text-confirmation"
                >
                    <CheckIcon className="h-3.5 w-3.5" />
                    Accept
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStateChange("rejected")}
                    className="h-7 gap-1 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                    <XIcon className="h-3.5 w-3.5" />
                    Reject
                </Button>
            </div>
        );
    }

    if (state === "accepted") {
        return (
            <Button
                variant="default"
                size="sm"
                onClick={() => onStateChange("pending")}
                className="h-7 gap-1.5 bg-confirmation px-2 text-confirmation-foreground hover:bg-confirmation/90"
            >
                <CheckIcon className="h-3.5 w-3.5" />
                Accepted
            </Button>
        );
    }

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={() => onStateChange("pending")}
            className="h-7 gap-1.5 border-destructive/50 px-2 text-destructive hover:bg-destructive/10"
        >
            <XIcon className="h-3.5 w-3.5" />
            Rejected
        </Button>
    );
}

interface AppliedBadgeProps {
    isApplied: boolean;
}

function AppliedBadge({ isApplied }: AppliedBadgeProps) {
    return (
        <div
            className={cn(
                "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                isApplied ? "bg-confirmation/20 text-confirmation" : "bg-muted text-muted-foreground"
            )}
        >
            {isApplied ? (
                <>
                    <CheckIcon className="h-3 w-3" />
                    Applied
                </>
            ) : (
                <>
                    <XIcon className="h-3 w-3" />
                    Skipped
                </>
            )}
        </div>
    );
}
